/**
 * 路由表。
 *
 * 未登录一律跳 `/login`；登录后按权限渲染菜单，
 * 但没权限的路由仍然可以被直接输入 URL 访问——此时接口会返回 403，
 * 页面显示服务端给的错误信息。**前端不做权限兜底判断**，
 * 否则会出现「前端以为有权限但后端拒绝」的不一致。
 */

import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { Spin } from "antd";
import { useAuth } from "./services/auth";
import { AppLayout } from "./components/AppLayout";
import { LoginPage } from "./features/auth/LoginPage";
import { OverviewPage } from "./features/overview/OverviewPage";
import { TableCensusPage } from "./features/overview/TableCensusPage";
import { RelationshipsPage } from "./features/relationships/RelationshipsPage";
import { RelationshipDetailPage } from "./features/relationships/RelationshipDetailPage";
import { ConversationsPage } from "./features/conversations/ConversationsPage";
import { ConversationDetailPage } from "./features/conversations/ConversationDetailPage";
import { MessageSearchPage } from "./features/conversations/MessageSearchPage";
import { FeedbackPage } from "./features/conversations/FeedbackPage";
import { BrainPage } from "./features/brain/BrainPage";
import { BrainRunDetailPage } from "./features/brain/BrainRunDetailPage";
import { MemoriesPage } from "./features/memories/MemoriesPage";
import { RelationshipDiariesPage } from "./features/diaries/DiariesPage";
import { CharacterDiariesPage } from "./features/diaries/CharacterDiariesPage";
import { CharacterMomentsPage } from "./features/diaries/CharacterMomentsPage";
import { CharacterPlansPage } from "./features/diaries/CharacterPlansPage";
import { CharactersPage } from "./features/characters/CharactersPage";
import { CharacterDetailPage } from "./features/characters/CharacterDetailPage";
import { ReservedNamesPage } from "./features/characters/ReservedNamesPage";
import { ProductsPage } from "./features/products/ProductsPage";
import { SongsPage } from "./features/songs/SongsPage";
import { KnowledgePage } from "./features/knowledge/KnowledgePage";
import { KnowledgeDocumentPage } from "./features/knowledge/KnowledgeDocumentPage";
import { SkillsPage } from "./features/skills/SkillsPage";
import { AssetsPage } from "./features/assets/AssetsPage";
import { ImageGenerationsPage } from "./features/image-generations/ImageGenerationsPage";
import { CommercePage } from "./features/commerce/CommercePage";
import { OperationsPage } from "./features/operations/OperationsPage";
import { SecurityPage } from "./features/security/SecurityPage";
import { MigrationPage } from "./features/migration/MigrationPage";
import { ContentDraftsPage } from "./features/content/ContentDraftsPage";
import { ContentDraftPage } from "./features/content/ContentDraftPage";
import { QqBotPage } from "./features/qq-bot/QqBotPage";
import { LiveManagementPage } from "./features/live/LiveManagementPage";

function LegacyRelationshipRedirect() {
  const { id = "" } = useParams();
  return <Navigate to={`/users/${id}`} replace />;
}

export function AppRoutes() {
  const { admin, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh", gap: 12 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!admin) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/diaries/plans" replace />} />
      <Route element={<AppLayout />}>
        <Route path="/overview" element={<OverviewPage />} />
        <Route path="/tables" element={<TableCensusPage />} />

        <Route path="/users" element={<RelationshipsPage />} />
        <Route path="/users/:id" element={<RelationshipDetailPage />} />

        <Route path="/relationships" element={<Navigate to="/users" replace />} />
        {/* 兼容旧收藏链接；新页面内生成的链接统一使用 /users/:id。 */}
        <Route path="/relationships/:id" element={<LegacyRelationshipRedirect />} />

        <Route path="/conversations" element={<ConversationsPage />} />
        <Route path="/conversations/search" element={<MessageSearchPage />} />
        <Route path="/conversations/feedback" element={<FeedbackPage />} />
        <Route path="/conversations/:id" element={<ConversationDetailPage />} />

        <Route path="/brain" element={<BrainPage />} />
        <Route path="/brain/runs/:id" element={<BrainRunDetailPage />} />

        <Route path="/memories" element={<MemoriesPage />} />
        <Route path="/diaries" element={<Navigate to="/diaries/relationship" replace />} />
        <Route path="/diaries/relationship" element={<RelationshipDiariesPage />} />
        <Route path="/diaries/character" element={<CharacterDiariesPage />} />
        <Route path="/diaries/moments" element={<CharacterMomentsPage />} />
        <Route path="/diaries/plans" element={<CharacterPlansPage />} />

        <Route path="/characters" element={<CharactersPage />} />
        <Route path="/characters/reserved-names" element={<ReservedNamesPage />} />
        <Route path="/characters/:id" element={<CharacterDetailPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/songs" element={<SongsPage />} />

        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/knowledge/documents/:id" element={<KnowledgeDocumentPage />} />

        <Route path="/skills" element={<SkillsPage />} />
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/image-generations" element={<ImageGenerationsPage />} />
        <Route path="/content" element={<ContentDraftsPage />} />
        <Route path="/content/:id" element={<ContentDraftPage />} />
        <Route path="/commerce" element={<CommercePage />} />
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/channels/live" element={<LiveManagementPage />} />
        <Route path="/channels/qq" element={<QqBotPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/migration" element={<MigrationPage />} />

        <Route path="*" element={<Navigate to="/diaries/plans" replace />} />
      </Route>
    </Routes>
  );
}
